import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Person } from '../people/people';
import { Competition } from '../competitions/competitions';
import { SiteRole } from '../auth/auth';

export type Plan = 'FREE' | 'TIER_1' | 'SITE_ADMIN';

/** Person as returned by the admin stats endpoint — same shape as the
 *  catalog's Person, plus createdAt (needed for the "recently added" list;
 *  dateOfDeath already covers "recently deceased"). */
export type AdminPerson = Person & { createdAt: string };

export interface ContestantsByCompetition {
  competitionId: number;
  name: string;
  count: number;
}

export interface AdminStats {
  recentPeople: AdminPerson[];
  recentDeaths: AdminPerson[];
  openCompetitionCount: number;
  totalContestants: number;
  contestantsByCompetition: ContestantsByCompetition[];
}

export interface ListCompetitionsResult {
  items: Competition[];
  total: number;
}

/** One contestant's pick of a person, for the People page's research panel.
 *  The API already returns these sorted: latest competition first, then
 *  competition name, then contestant name. */
export interface PersonPicker {
  pickId: number;
  /** A reserve pick, not (yet) in the contestant's regular list. */
  isAlternate: boolean;
  competition: { id: number; name: string; startDate: string; endDate: string };
  contestant: { id: number; displayName: string };
}

/** What "Recalculate scores" did: how many deaths it replayed and the
 *  competition's total award count before and after. */
export interface RecalculationResult {
  deathsProcessed: number;
  awardsBefore: number;
  awardsAfter: number;
}

/** F-018 — a row on the Manage Users list. */
export interface UserSummary {
  id: number;
  email: string;
  displayName: string;
  siteRole: SiteRole;
  plan: Plan;
  emailVerifiedAt: string | null;
  /** First and most recent password sign-in; null = never signed in (or
   *  hasn't since sign-ins started being recorded). */
  firstLoginAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  /** Competitions this user owns. */
  ownedCount: number;
  /** Competitions this user is a contestant of but doesn't own. */
  joinedCount: number;
}

export type UserDetail = Omit<UserSummary, 'ownedCount' | 'joinedCount'>;

export interface ListUsersResult {
  items: UserSummary[];
  total: number;
}

export interface CreateUserInput {
  email: string;
  displayName: string;
  password: string;
  siteRole?: SiteRole;
  plan?: Plan;
}

export interface UpdateUserInput {
  email?: string;
  displayName?: string;
  siteRole?: SiteRole;
  plan?: Plan;
}

/** F-021 — a plan's contestant/active-competition/achievement caps. */
export interface PlanConfig {
  plan: Plan;
  maxContestants: number;
  maxActiveCompetitions: number;
  maxAchievements: number;
}

export interface UpdatePlanConfigInput {
  maxContestants?: number;
  maxActiveCompetitions?: number;
  maxAchievements?: number;
}

/** F-009 — site-admin-only endpoints. Mirrors PeopleService/CompetitionsService's pattern. */
@Injectable({ providedIn: 'root' })
export class AdminService {
  private readonly http = inject(HttpClient);

  getStats(): Promise<AdminStats> {
    return firstValueFrom(
      this.http.get<AdminStats>(`${environment.apiBaseUrl}/admin/stats`),
    );
  }

  /** F-009 Milestone C, Slice 6 — unscoped, paginated competitions list. */
  listCompetitions(
    params: { q?: string; page?: number; limit?: number } = {},
  ): Promise<ListCompetitionsResult> {
    return firstValueFrom(
      this.http.get<ListCompetitionsResult>(
        `${environment.apiBaseUrl}/admin/competitions`,
        {
          params: {
            ...(params.q ? { q: params.q } : {}),
            ...(params.page ? { page: params.page } : {}),
            ...(params.limit ? { limit: params.limit } : {}),
          },
        },
      ),
    );
  }

  /** Everyone, across every competition, who has this person picked. */
  listPersonPickers(personId: number): Promise<PersonPicker[]> {
    return firstValueFrom(
      this.http.get<PersonPicker[]>(
        `${environment.apiBaseUrl}/admin/people/${personId}/pickers`,
      ),
    );
  }

  /** Replays every valid death in the competition through scoring so picks
   *  entered after a death was recorded still get scored. Safe to repeat. */
  recalculateScores(competitionId: number): Promise<RecalculationResult> {
    return firstValueFrom(
      this.http.post<RecalculationResult>(
        `${environment.apiBaseUrl}/admin/competitions/${competitionId}/recalculate-scores`,
        {},
      ),
    );
  }

  /** F-018 — unscoped, paginated users list, with owned/joined counts. */
  listUsers(
    params: { q?: string; page?: number; limit?: number } = {},
  ): Promise<ListUsersResult> {
    return firstValueFrom(
      this.http.get<ListUsersResult>(`${environment.apiBaseUrl}/admin/users`, {
        params: {
          ...(params.q ? { q: params.q } : {}),
          ...(params.page ? { page: params.page } : {}),
          ...(params.limit ? { limit: params.limit } : {}),
        },
      }),
    );
  }

  getUser(id: number): Promise<UserDetail> {
    return firstValueFrom(
      this.http.get<UserDetail>(`${environment.apiBaseUrl}/admin/users/${id}`),
    );
  }

  createUser(input: CreateUserInput): Promise<UserDetail> {
    return firstValueFrom(
      this.http.post<UserDetail>(`${environment.apiBaseUrl}/admin/users`, input),
    );
  }

  updateUser(id: number, input: UpdateUserInput): Promise<UserDetail> {
    return firstValueFrom(
      this.http.patch<UserDetail>(
        `${environment.apiBaseUrl}/admin/users/${id}`,
        input,
      ),
    );
  }

  deleteUser(id: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${environment.apiBaseUrl}/admin/users/${id}`),
    );
  }

  sendPasswordReset(id: number): Promise<{ message: string }> {
    return firstValueFrom(
      this.http.post<{ message: string }>(
        `${environment.apiBaseUrl}/admin/users/${id}/send-password-reset`,
        {},
      ),
    );
  }

  /** F-021 — every plan's current limits. */
  listPlanConfig(): Promise<PlanConfig[]> {
    return firstValueFrom(
      this.http.get<PlanConfig[]>(`${environment.apiBaseUrl}/admin/plan-config`),
    );
  }

  updatePlanConfig(plan: Plan, input: UpdatePlanConfigInput): Promise<PlanConfig> {
    return firstValueFrom(
      this.http.patch<PlanConfig>(
        `${environment.apiBaseUrl}/admin/plan-config/${plan}`,
        input,
      ),
    );
  }
}
